import {
  DateRangePicker,
  Icon,
  IconSizeClasses,
  QuickSelectOption,
} from '@eppendorf/vnls-react-components';
import { ButtonV2, useBreakpoint } from '@eppendorf/vnls-react-components-preview';
import { hasPermission } from '@eppendorf/vnls-user-tenant-utils';
import { parseISO, subDays, subMonths } from 'date-fns';
import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';
import { FeatureToggle } from '$shared/feature-toggle/types';

import { useAdditionalFeatureIsEnabled } from '$components/additional-feature-guard/additional-feature-is-enabled';
import { FeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { Overview } from '$components/overview-box/overview-box';
import { SearchInput } from '$components/search-input/search-input';
import { AuditEventsList } from '$features/audit-events/audit-events-list';
import { ExportAuditEventsDialog } from '$features/audit-events/components/export-audit-events-dialog';
import {
  AuditEventCategory,
  AuditEventsCategoryFilter,
  AUDIT_EVENT_CATEGORIES,
} from '$features/audit-events/components/filters/category-filter';
import {
  ReportHubBetaAccessDialog,
  ReportHubUpsellDialog,
} from '$features/compliance-upsell/report-hub-upsell-nav-item';
import { useDateRangeChange } from '$features/monitoring/history-charts-v2/useDateRangeChange';
import {
  isEssentialsPlan,
  useGetSubscription,
} from '$features/subscription/subscription-api';

import './audit-events-overview.scss';

export function AuditEventsOverview(): ReactElement {
  const { t } = useTranslation();

  const isMinSm = useBreakpoint({ min: 'sm' });

  const [searchParams, setSearchParams] = useSearchParams();
  const to = searchParams.get('to');
  const from = searchParams.get('from');
  const searchTerm = searchParams.get('searchTerm');
  const category = searchParams.get('category') as AuditEventCategory | null;
  const { onRangeChange } = useDateRangeChange(true);
  const disabledDates = {
    before: subDays(new Date(), 89),
    after: new Date(),
  };
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isUpsellDialogOpen, setIsUpsellDialogOpen] = useState(false);
  const [isBetaAccessDialogOpen, setIsBetaAccessDialogOpen] = useState(false);
  const { data: subscription } = useGetSubscription();
  const hasReportHubAccess = useAdditionalFeatureIsEnabled('REPORT_HUB_ACCESS');

  // This callback is passed to SearchInput and updates the URL search parameters
  const handleSearchQueryChange = useCallback(
    (newSearchQuery: string) => {
      setSearchParams(
        (prev) => {
          const trimmedQuery = newSearchQuery.trim();
          if (trimmedQuery.length > 0) {
            prev.set('searchTerm', trimmedQuery);
          } else {
            prev.delete('searchTerm');
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleCategoryChange = useCallback(
    (newCategory: AuditEventCategory) => {
      setSearchParams(
        (prev) => {
          if (newCategory === AuditEventCategory.All) {
            prev.delete('category');
          } else {
            prev.set('category', newCategory);
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleExportClick = () => {
    if (isEssentialsPlan(subscription)) {
      setIsUpsellDialogOpen(true);
      return;
    }

    if (!hasReportHubAccess) {
      setIsBetaAccessDialogOpen(true);
      return;
    }

    setIsExportDialogOpen(true);
  };

  const [selectedQuickOption, setSelectedQuickOption] = useState<
    QuickSelectOption | undefined
  >(undefined);

  const handleQuickSelectOption = (option: QuickSelectOption) => {
    setSelectedQuickOption(option);
  };

  /**
   * We're using useTokenAttributes and hasPermission directly instead of calling useAccessControlGuard
   * because userPermissions is set to false as an intial state which will redirect the user to the devices page
   * without waiting for the hasPermission check to complete.
   */
  const { permissions: userPermissions } = useTokenAttributes(['permissions']);
  const isAllowed = hasPermission('VIEW_AUDIT_TRAIL_OVERVIEW', userPermissions);
  const hasPermissionToViewAuditTrailOverView =
    userPermissions === undefined ? true : isAllowed;

  if (!hasPermissionToViewAuditTrailOverView) {
    return <Navigate to="/devices" replace />;
  }

  const selectedCategory =
    category && AUDIT_EVENT_CATEGORIES.includes(category)
      ? category
      : AuditEventCategory.All;

  const isMaxMd = useBreakpoint({ max: 'md' });

  return (
    <Overview className={`box--mobile-fullscreen ${isMaxMd ? 'box--no-shadow' : ''}`}>
      <Overview.Header>
        <div className="flex flex__dir--column gap-m w-full">
          <div className="flex flex__jc--space-between flex__ai--center">
            <h1 className="title">{t('auditEvents.overview')}</h1>
            <FeatureIsEnabled feature={'ESEVNLS_1888_AUDIT_EXPORT' as FeatureToggle}>
              <ButtonV2
                variant="primary"
                className={`${isMinSm ? 'p-left-sm' : ''}`}
                onPress={() => handleExportClick()}
                iconOnly={!isMinSm}
              >
                <Icon name="export" size={IconSizeClasses.XSmall} className="bg-white" />

                {isMinSm && t('shared.export', 'Export')}
              </ButtonV2>
            </FeatureIsEnabled>
          </div>
          <div className="audit-events-overview__search-filter-bar">
            <AuditEventsCategoryFilter
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              className="report-status-filter-component"
            />
            <DateRangePicker
              dateRange={{
                from: from ? parseISO(from) : undefined,
                to: to ? parseISO(to) : undefined,
              }}
              onDateRangeChange={onRangeChange}
              autoFocus
              defaultMonth={subMonths(disabledDates.after, 1)}
              disabled={disabledDates}
              startMonth={disabledDates.before}
              endMonth={disabledDates.after}
              quickSelect={{
                enabled: true,
                onOptionSelect: handleQuickSelectOption,
                selectedOption: selectedQuickOption,
              }}
            />
            <SearchInput
              onSearchQueryChange={handleSearchQueryChange}
              placeholder={t('auditEvents.searchPlaceholder')}
              id="audit-events-search"
              value={searchTerm ?? ''}
              className="audit-events-search bg-white"
            />
          </div>
        </div>
      </Overview.Header>

      <Overview.Body>
        <AuditEventsList
          from={from}
          to={to}
          searchTerm={searchTerm}
          category={category}
          setSearchParams={setSearchParams}
        />
      </Overview.Body>

      <ExportAuditEventsDialog
        isOpen={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
      />
      <ReportHubUpsellDialog
        isOpen={isUpsellDialogOpen}
        onOpenChange={setIsUpsellDialogOpen}
      />
      <ReportHubBetaAccessDialog
        isOpen={isBetaAccessDialogOpen}
        onOpenChange={setIsBetaAccessDialogOpen}
      />
    </Overview>
  );
}
